/** Mirrors the backend's `shared.mapping.alarm_config_group_catalog` (xmx-dpf-alarm) — maps an
 * individual alarm type key (as seen on an open alarm / the status API) to the `config_group`
 * it's governed by, so the overview tab can show the same grouped, human-readable label as the
 * settings tab instead of the raw per-event catalog key (which for cloud types like
 * `device_connection`/`FRAUD_EVENT` is literally the key itself — see alarm_type_catalog.py). */

const DEVICE_RULE_STATUS_PREFIX = 'device.rule.status.';

/** Cloud-computed types whose catalog key carries no `<log>.` prefix but still belongs to a
 * DLMS-log config group (see alarm_config_group_catalog.py's explicit `catalog_keys`). */
const EXPLICIT_CONFIG_GROUPS: Record<string, string> = {
  FRAUD_EVENT: 'dlms.fraud',
  METER_FAILURE: 'dlms.standard',
};

const CONFIG_GROUP_PREFIXES: Record<string, string> = {
  'standard.': 'dlms.standard',
  'fraud.': 'dlms.fraud',
  'power_quality.': 'dlms.power_quality',
  'communication.': 'dlms.communication',
};

export function configGroupForAlarmType(typeKey: string): string {
  const key = typeKey.startsWith(DEVICE_RULE_STATUS_PREFIX)
    ? typeKey.slice(DEVICE_RULE_STATUS_PREFIX.length)
    : typeKey;

  if (key in EXPLICIT_CONFIG_GROUPS) return EXPLICIT_CONFIG_GROUPS[key];

  const prefix = Object.keys(CONFIG_GROUP_PREFIXES).find(p => key.startsWith(p));
  return prefix ? CONFIG_GROUP_PREFIXES[prefix] : key;
}

/** Translation key for a config_group's display label (`alarmsPage.settings.alarmTypes.groupLabels.*`) —
 * shared so the overview and settings tabs never drift into showing different names for the same group. */
export function alarmConfigGroupLabelKey(configGroup: string): string {
  return `alarmsPage.settings.alarmTypes.groupLabels.${configGroup.replaceAll('.', '_')}`;
}

/** Only DLMS event-log-backed keys carry the log itself as part of the key (e.g. "fraud.44",
 * "power_quality.overvoltage_lx") and get a "<Category> Event: <label>" prefix. Cloud types
 * that share the same config_group via EXPLICIT_CONFIG_GROUPS (FRAUD_EVENT, METER_FAILURE) have
 * no "<log>." prefix of their own and keep their plain catalog label instead — prefixing those
 * would just duplicate the label. Strips the "DLMS " lead-in from the shared groupLabels
 * translation (e.g. "DLMS Fraud Detection Event") since this inline prefix should read as
 * "Fraud Detection Event: ...", not "DLMS Fraud Detection Event: ...".
 * Shared by AlarmsOverviewTabComponent and DeviceAlarmsTabComponent so catalog-label formatting
 * can't drift between the two. */
export function dlmsCategoryLabel(key: string, translate: (key: string) => string): string | undefined {
  if (!key.includes('.')) return undefined;
  const configGroup = configGroupForAlarmType(key);
  if (!configGroup.startsWith('dlms.')) return undefined;
  const groupLabel = translate(alarmConfigGroupLabelKey(configGroup));
  return groupLabel.replace(/^DLMS\s+/, '');
}

/** Renders the human label for a raw catalog key (e.g. "fraud.44" -> "Fraud Detection Event:
 * Meter cover removed"). Shared by AlarmsOverviewTabComponent and DeviceAlarmsTabComponent.
 *
 * Non-DLMS keys (e.g. `device_connection`, `no_data_alarm`) have no per-event detail beyond
 * their config group, so the group's own translated label (the same one the settings tab shows,
 * e.g. "Device offline"/"Geen meetdata") is preferred over the alarm-type catalog's label, which
 * may not be translated for these cloud-computed types. */
export function displayAlarmTypeName(
  key: string,
  labelForKey: (key: string) => string,
  translate: (key: string) => string,
): string {
  const category = dlmsCategoryLabel(key, translate);
  if (category) return `${category}: ${labelForKey(key)}`;

  const groupLabelKey = alarmConfigGroupLabelKey(configGroupForAlarmType(key));
  const groupLabel = translate(groupLabelKey);
  return groupLabel !== groupLabelKey ? groupLabel : labelForKey(key);
}

/** Log-backed alarm types reset the device's onboard event log on close (see xmx-dpf-alarm
 * shared/mapping/alarm_type_catalog.reset_log_for) — derived from the same config_group mapping
 * used everywhere else so this can't drift out of sync with it. */
export function isLogBackedAlarmType(rawTypeKey: string): boolean {
  const key = rawTypeKey.replace('status.names.', '');
  return configGroupForAlarmType(key).startsWith('dlms.');
}
